import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import DataTable from 'components/common/DataTable';
import ActionsDropdown from 'components/common/ActionsDropdown';
import axios from 'axios';
import * as Routes from 'modules/routes';
import DropdownButton from '@/components/ui/Dropdown/DropdownButton';
import Icon from '@/components/ui/Icon';
import DeleteModal from '@/components/ui/Modal/SubmitModal';
import Tooltip, { TCITooltip } from '@/components/ui/Tooltip';
import Text from '@/components/ui/Text';
import { COLORS } from '@/components/ui/ui.constants';
import useFlashMessage from '@/components/shared/FlashMessage/useFlashMessage';
import TableActions from './TableActions';
import GameTemplate from '../../interfaces/GameTemplate';
import { ILessonGamesGlossaryTerm } from './interfaces';
import useLessonGamesStore from '../../../stores/useLessonGamesStore';

type TableTypes = {
  gameTemplates: GameTemplate[],
  glossaryTerms: ILessonGamesGlossaryTerm[],
  lessonId: number;
  programId: number;
  hasChallenge?: boolean;
  hasVocabGame: boolean;
};

const LessonGamesTable = ({
  gameTemplates,
  glossaryTerms,
  lessonId,
  programId,
  hasChallenge,
  hasVocabGame: initialHasVocabGame,
}: TableTypes) => {
  const [gamesTableData, setGamesTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [hasVocabGame, setHasVocabGame] = useState(initialHasVocabGame);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [currRowData, setCurrRowData] = useState(null);
  const { setStore } = useLessonGamesStore();

  const container = document.getElementById('content_container');
  const { setFlashMessage } = useFlashMessage(
    { message: 'Game deleted successfully', type: 'notice' },
    container,
    'game-deleted',
  );

  useEffect(() => {
    setStore({
      gameTemplates,
      glossaryTerms,
      lessonId,
      programId,
      refetch: () => {
        setShouldFetch(true);
        setHasVocabGame(true);
      },
    });
  }, []);

  useEffect(() => {
    if (!shouldFetch) return;

    setIsLoading(true);

    axios
      .get(Routes.plato_api_lesson_games_games_path({ lesson_id: lessonId, program_id: programId }))
      .then((res) => {
        setGamesTableData(res.data.data);
        setIsLoading(false);
        setShouldFetch(false);
      });
  }, [shouldFetch]);

  const handleDelete = (rowData) => {
    const { id, game_type } = rowData;
    setDeleteInProgress(true);

    const apiPath = game_type === 'Lesson Review Game'
      ? Routes.plato_api_challenge_path(id)
      : Routes.plato_api_game_path(id);

    axios.delete(apiPath)
      .then(() => {
        setFlashMessage();
        window.location.reload();
      }).finally(() => {
        setDeleteInProgress(false);
        setIsDeleteModalOpen(false);
      });
  };

  const isPublishedOrHasAnswers = rowData => (
    rowData.published === 'Published' || rowData.has_answers
  );

  const editPath = (rowData) => {
    if (rowData.game_type === 'Lesson Review Game') {
      return (
        Routes.edit_admin_lesson_challenge_path(lessonId, { program_id: programId })
      );
    }

    return (
      Routes.edit_admin_game_path(rowData.id, { lesson_id: lessonId, program_id: programId })
    );
  };

  const actionsDropdown = rowData => (
    <ActionsDropdown
      dropdownClassName="tw-overflow-visible"
      id={`slide-group-${rowData.id}-actions`}
      toggleText="Actions"
    >
      <a
        className="dropdown-item"
        href={editPath(rowData)}
      >
        <i aria-hidden="true" className="fa fa-edit" /> Edit
      </a>
      <TCITooltip disabled={!isPublishedOrHasAnswers(rowData)}>
        <Tooltip.Trigger asChild>
          <div>
            <DropdownButton
              className="tw-flex tw-items-center"
              disabled={isPublishedOrHasAnswers(rowData)}
              onClick={() => {
                setCurrRowData(rowData);
                setIsDeleteModalOpen(true);
              }}
            >
              <Icon className="tw-mr-1.5 tw-relative tw-top-0.5" fill={COLORS.red} icon="Delete" size={15} />
              Delete
            </DropdownButton>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content> { rowData.published === 'Published'
          ? 'You cannot delete a published game.'
          : 'You cannot delete a game that has student answers.' }
        </Tooltip.Content>
      </TCITooltip>
    </ActionsDropdown>
  );

  return (
    <div className="pb20">
      <TableActions hasChallenge={hasChallenge} hasVocabGame={hasVocabGame} />
      <DataTable
        handleResourcesManually
        columnDefs={[
          {
            sortable: false,
            targets: 0,
            createdCell: (td, _cellData, rowData) => {
              ReactDOM.render(
                <a href={editPath(rowData)}>
                  {rowData.name}
                </a>,
                td,
              );
            },
          },
          { sortable: false, targets: 1 },
          { sortable: false, targets: 2 },
          { sortable: false, targets: 3 },
          {
            sortable: false,
            targets: 4,
            createdCell: (td, _cellData, rowData) => {
              ReactDOM.render(
                actionsDropdown(rowData),
                td,
              );
            },
          },
        ]}
        columnMapping={['name', 'type', 'status', 'questions']}
        columns={['name', 'game_type', 'published', 'question_count', 'actions']}
        defaultOrder={[0, 'asc']}
        info={false}
        isLoading={isLoading}
        paging={false}
        resources={gamesTableData}
        sortable={false}
      />
      {currRowData && (
      <DeleteModal
        close={() => setIsDeleteModalOpen(false)}
        headerText={`Remove ${currRowData.game_type}`}
        isLoading={deleteInProgress}
        isOpen={isDeleteModalOpen}
        SubmitButtonProps={{ color: 'red' }}
        submitText="Delete"
        submittingText="Deleting…"
        onSubmit={(e) => {
          e.preventDefault();
          handleDelete(currRowData);
        }}
      >
        <Text className="tw-flex" size="sm">
          Are you sure you want to remove this&nbsp;
          <span className="tw-font-bold">{currRowData.game_type.toLowerCase()}</span>?
        </Text>
      </DeleteModal>
      )}
    </div>
  );
};

export default LessonGamesTable;
